import { useState, useEffect, useCallback } from 'react';
import { dashboardApi } from '../services/api';
import type { 
  DashboardStats,
  DashboardAnalytics,
  DashboardFilters,
  DashboardStatsResponse,
  DashboardAnalyticsResponse,
  PerformanceMetrics
} from '../types/dashboard';

interface UseDashboardStatsState {
  stats: DashboardStats | null;
  analytics: DashboardAnalytics | null;
  loading: boolean;
  analyticsLoading: boolean;
  error: string | null;
  filters: DashboardFilters;
}

interface UseDashboardStatsReturn extends UseDashboardStatsState {
  loadStats: () => Promise<void>;
  loadAnalytics: () => Promise<void>;
  getPerformanceMetrics: (filters?: DashboardFilters) => Promise<PerformanceMetrics[]>;
  setFilters: (filters: Partial<DashboardFilters>) => void;
  refreshData: () => Promise<void>;
}

export const useDashboardStats = (initialFilters: DashboardFilters = {}): UseDashboardStatsReturn => {
  const [state, setState] = useState<UseDashboardStatsState>({
    stats: null,
    analytics: null,
    loading: false,
    analyticsLoading: false,
    error: null,
    filters: {
      period: 'daily',
      dateRange: {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
        end: new Date().toISOString() // today
      },
      ...initialFilters
    }
  });

  const loadStats = useCallback(async () => {
    if (state.loading) return;
    
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await dashboardApi.getStats(state.filters) as DashboardStatsResponse;
      setState(prev => ({
        ...prev,
        stats: response.data || null,
        loading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load dashboard stats',
        loading: false
      }));
    }
  }, [state.filters, state.loading]);

  const loadAnalytics = useCallback(async () => {
    if (state.analyticsLoading) return;
    
    setState(prev => ({ ...prev, analyticsLoading: true, error: null }));
    
    try {
      const response = await dashboardApi.getAnalytics(state.filters) as DashboardAnalyticsResponse;
      setState(prev => ({
        ...prev,
        analytics: response.data || null,
        analyticsLoading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load analytics',
        analyticsLoading: false
      }));
    }
  }, [state.filters, state.analyticsLoading]);

  const getPerformanceMetrics = useCallback(async (filters?: DashboardFilters): Promise<PerformanceMetrics[]> => {
    try {
      const response = await dashboardApi.getPerformanceMetrics(filters) as any;
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch performance metrics');
      }
    } catch (error) {
      console.error('Failed to get performance metrics:', error);
      throw error;
    }
  }, []);

  const setFilters = useCallback((newFilters: Partial<DashboardFilters>) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        ...newFilters
      }
    }));
  }, []);

  const refreshData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Fetch both stats and analytics in parallel
      await Promise.all([
        loadStats(),
        loadAnalytics()
      ]);
    } catch (error) {
      console.error('Failed to refresh dashboard data:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to refresh dashboard data',
        loading: false
      }));
    }
  }, [loadStats, loadAnalytics]);

  // Fetch initial data when filters change
  useEffect(() => {
    refreshData();
  }, [state.filters]);

  return {
    ...state,
    loadStats,
    loadAnalytics,
    getPerformanceMetrics,
    setFilters,
    refreshData
  };
};

// Hook for real-time dashboard updates
export const useRealtimeDashboard = (refreshInterval: number = 30000) => {
  const dashboardStats = useDashboardStats();
  
  useEffect(() => {
    const interval = setInterval(() => {
      dashboardStats.refreshData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [dashboardStats, refreshInterval]);

  return dashboardStats;
};

// Hook for specific dashboard metrics
export const useDashboardMetrics = () => {
  const [metrics, setMetrics] = useState<{
    totalContent: number;
    activeCampaigns: number;
    totalImpressions: number;
    averageCTR: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getMetrics = useCallback(async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await dashboardApi.getQuickMetrics() as any;
      if (response.success && response.data) {
        setMetrics(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch metrics');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    getMetrics();
  }, [getMetrics]);

  return {
    metrics,
    loading,
    error,
    refreshMetrics: getMetrics
  };
};

export default useDashboardStats;